'use client'

import type { HeroFieldsFragment } from '@/graphql/types'
import {
  wrapExperience,
  type BaselineMapper,
  type ExperienceConfiguration,
  Reference,
} from '@ccpgames/eve-web-contentful'
import { parseExperiences } from '@/helpers/experiences'
import { createExperienceAwareEntry } from '@/components/templates/dynamic/experienceEntry'
import { Hero } from './Hero'

/**
 * Maps the baseline entry to include an `id` property at the top level.
 * This is required by the experience wrapper to track which entry is being rendered.
 * The id is extracted from `entry.sys.id` and added as a top-level property.
 */
const mapHeroBaseline: BaselineMapper<HeroFieldsFragment, HeroFieldsFragment & { id: string }> = (entry) => {
  const mapped = { id: entry.sys.id, ...entry }
  return mapped
}

/**
 * Parses and extracts experience configurations from the entry.
 * Experiences define A/B test variants and personalization rules from Contentful.
 * Returns an array of experience configurations that can be used to determine
 * which variant should be shown to the user.
 */
const parseHeroExperiences = (entry: HeroFieldsFragment | undefined): ExperienceConfiguration<Reference>[] => {
  const experiences = parseExperiences<HeroFieldsFragment>(entry)
  return experiences
}

/**
 * Wraps the Hero component with experience logic.
 * This enables A/B testing and personalization by:
 * - Tracking which variant is being shown
 * - Applying experience rules (audience targeting, traffic allocation, etc.)
 * - Rendering the appropriate variant based on the user's profile
 * The render function receives the baseline entry (or variant) and renders the Hero component.
 */
const HeroWithExperience = wrapExperience<HeroFieldsFragment, HeroFieldsFragment & { id: string }>(
  (baseline) => {
    return <Hero entry={baseline} />
  },
  mapHeroBaseline,
  parseHeroExperiences,
)

/**
 * Client component that handles Hero entries with experiences.
 * This component is used when the entry has experience configurations defined.
 * It wraps the Hero component with experience logic to enable A/B testing and personalization.
 */
export function HeroExperience({ entry }: { entry: HeroFieldsFragment }) {
  return <HeroWithExperience entry={entry} />
}

/**
 * Entry point component that automatically decides whether to use experience logic.
 * - If the entry has no experiences: renders the base Hero component directly
 * - If the entry has experiences: renders HeroExperience to handle A/B testing and personalization
 * This is the component that should be used in the factory/rendering layer.
 */
export const HeroEntry = createExperienceAwareEntry<HeroFieldsFragment>(Hero, HeroExperience)
